import { DrawerProps, styled } from '@mui/material';
import { Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Drawer, drawerClasses } from '@o2/ui';
import { FC, ReactNode } from 'react';
import Header from './Header';
import Content from './Content';
import Footer from './Footer';

export interface SidesheetProps extends DrawerProps {
  /**
   * If true, the component is shown.
   */
  open: boolean;
  /**
   * Callback fired when Close button is pressed.
   */
  onClose?: () => void;
  /**
   * React Node of children
   */
  children: ReactNode;
  /**
   * React Node of header
   */
  header?: ReactNode;
  /**
   * React Node of footer
   */
  footer?: ReactNode;
  /**
   * ID of the Drawer component, propagated to the DOM as `data-elm`.
   */
  drawerElmId?: string;
  /**
   * Z-index to use for the Drawer inside. Defaults to MUI's default (https://mui.com/material-ui/customization/z-index/),
   * which is 1200 as of 3/2024.
   */
  drawerZIndex?: number;
}

const StyledDrawer = styled(Drawer, {
  name: 'O2Sidesheet',
  slot: 'root',
})(({ theme }) => ({
  [`& .${drawerClasses.paper}`]: {
    width: '100%',
    maxHeight: 'calc(100% - 48px)',

    [theme.breakpoints.up('sm')]: {
      width: '370px',
      maxHeight: '100%',
    },
  },
}));

type SidesheetComponent = FC<SidesheetProps> & {
  Header: typeof Header;
  Content: typeof Content;
  Footer: typeof Footer;
};

const Sidesheet: SidesheetComponent = ({ open, drawerElmId, drawerZIndex, children, ...props }) => {
  const smUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'));

  return (
    <StyledDrawer
      data-elm={drawerElmId}
      open={open}
      anchor={smUp ? 'right' : 'bottom'}
      sx={{
        ...(props.sx || {}),
        /*
         * yes, it has to be written like this, passing {zIndex:drawerZIndex} even when undefined causes an unset of Drawer's
         * default value and it gets reset to Modal's default (and that, as of 3/2024, is 1300 instead of the expected 1200).
         * see https://mui.com/material-ui/customization/z-index/
         */
        ...(drawerZIndex ? { zIndex: drawerZIndex } : undefined),
      }}
      {...props}
    >
      {children}
    </StyledDrawer>
  );
};

Sidesheet.Header = Header;
Sidesheet.Content = Content;
Sidesheet.Footer = Footer;

export default Sidesheet;
